import { calcParcelaFlex } from "./parcela-flex.ts";

// Simulador financeiro Servopa (Phase 2). Extraído do useMemo `calculos` do
// SimulatorContent pra ser puro e testável — são os números que o vendedor
// mostra ao cliente na proposta/PDF, então uma regressão aqui é invisível até
// um cliente contestar a parcela. O PDF (proposal-utils) consome o mesmo
// resultado, então simulador e PDF têm que concordar.

/** Fallback da taxa de seguro mensal (%) quando o grupo não tem taxa_seguro. */
export const DEFAULT_TAXA_SEGURO_PCT = 0.04;

/** Arredonda a 6 casas decimais — replica o ROUND(...;6) das células da planilha Servopa. */
const round6 = (x: number): number => Math.round(x * 1e6) / 1e6;

export interface SimulacaoInput {
  valorCredito: number;
  parcelaSemSeguro: number;
  valorSeguro: number;
  prazo: number;
  taxaAdmin: number;
  mesesContemplacao: number;
  juncaoCota: string;
  lanceLivre: string;
  lanceEmbutido: string;
  fixedLancePercent: number;
  /**
   * Percentual do Lance Fidelidade. Opcional: ausente/0 zera o cenário, e
   * quem não usa a modalidade não precisa passar nada — os testes e chamadores
   * antigos continuam válidos.
   */
  percLanceFidelidade?: number;
  descontoFlex: string;
  isFlex: boolean;
  isAuto: boolean;
  /** 'Sim' = com seguro. */
  seguroEfetivo: string;
  seguroForcadoPos: boolean;
  taxaSeguro: number | null | undefined;
}

export interface SimulacaoResult {
  valorCreditoJunto: number;
  parcelaJunta: number;
  parcelaAPagar: number;
  /** Parcelas restantes do cenário Sorteio (= prazo − mês). Mantido p/ retrocompat. */
  parcelasRestantes: number;
  parcelasRestantesSorteio: number;
  parcelasRestantesLanceFixo: number;
  parcelasRestantesLanceLivre: number;
  parcelasRestantesLanceFidelidade: number;
  valorLanceLivre: number;
  valorLanceEmbutido: number;
  valorLanceFixo: number;
  /**
   * Em quantas PARCELAS o lance ofertado (livre + embutido) foi arredondado, e
   * quanto vale cada uma.
   *
   * Existe porque a tela precisa dizer isso em voz alta. Um especialista
   * reportou "coloquei 37% e veio R$ 16.656, mudei pra 36% e não mudou nada" —
   * e não era erro de conta: 37% dava 18,49 parcelas e 36% dava 18,00, as duas
   * arredondam pra 18. Sem o número de parcelas na tela, o valor parece
   * aleatório; com ele, a regra fica óbvia na primeira olhada.
   */
  parcelasLanceOfertado: number;
  /**
   * Em quantas parcelas o embutido foi arredondado (o mesmo lanceEmParcelas que
   * já dava valorLanceEmbutido) — a tela do embutido diz isso como a do livre.
   */
  parcelasLanceEmbutido: number;
  /** Valor de uma parcela pós-contemplação — a unidade em que o lance é medido. */
  parcelaPrincipalPos: number;
  /**
   * De quantos em quantos pontos percentuais o valor do lance REALMENTE muda.
   *
   * É uma parcela expressa em % do total com taxa. No caso reportado dava 2,0%
   * — ou seja, metade dos passos de 1% do slider não movia nada. Sai daqui
   * pronto pra tela não ter que refazer a conta (e divergir dela).
   */
  passoLancePct: number;
  /**
   * Os percentuais do Lance Livre, saindo do RESULTADO e não do estado da
   * barra — mesma regra que Fixo e Fidelidade já seguem.
   *
   * O card do Lance Livre não mostrava percentual nenhum, e o "Lance embutido"
   * dava o MESMO valor do Lance Fixo (os dois são 30% do embutido do grupo).
   * Quem olhava concluía que o simulador tinha travado em 30% e ignorado os
   * 37% escolhidos — quando o 37% estava aplicado, só que no "Lance pago", sem
   * rótulo que dissesse isso.
   */
  lanceLivrePercent: number;
  lanceEmbutidoPercent: number;
  /** livre + embutido: é este que vira parcelas inteiras de uma vez. */
  lanceOfertadoPercent: number;
  lanceFixoPercent: number;
  creditoSorteio: number;
  saldoDevedorSorteio: number;
  parcelaAPagarSorteio: number;
  seguroSorteio: number;
  creditoLanceFixo: number;
  saldoDevedorLanceFixo: number;
  parcelaAPagarLanceFixo: number;
  seguroLanceFixo: number;
  valorLanceFidelidade: number;
  lanceFidelidadePercent: number;
  creditoLanceFidelidade: number;
  saldoDevedorLanceFidelidade: number;
  parcelaAPagarLanceFidelidade: number;
  seguroLanceFidelidade: number;
  creditoLanceLivre: number;
  saldoDevedorLanceLivre: number;
  parcelaAPagarLanceLivre: number;
  seguroLanceLivre: number;
  parcelaComSeguro: number;
}

/**
 * Converte um percentual de lance em quantidade inteira de parcelas + valor.
 *
 * A simulação oficial da Servopa (nota 5) arredonda o lance pra uma quantidade
 * inteira de parcelas — a base do percentual é o VALOR TOTAL COM TAXA (crédito ×
 * fatorTaxa), não o crédito puro. O valor exibido do lance é então
 * `parcelas × parcelaPrincipal`, e essas parcelas saem do prazo restante.
 */
function lanceEmParcelas(
  perc: number,
  valorTotalComTaxa: number,
  parcelaPrincipal: number,
): { valor: number; parcelas: number } {
  const alvo = Math.max(0, (perc / 100) * valorTotalComTaxa);
  if (perc <= 0 || parcelaPrincipal <= 0) return { valor: alvo, parcelas: 0 };
  const parcelas = Math.round(alvo / parcelaPrincipal);
  return { valor: parcelas * parcelaPrincipal, parcelas };
}

export function computeSimulacao(input: SimulacaoInput): SimulacaoResult {
  const {
    valorCredito, parcelaSemSeguro, valorSeguro, prazo, taxaAdmin,
    mesesContemplacao, juncaoCota, lanceLivre, lanceEmbutido,
    fixedLancePercent, descontoFlex, isFlex,
    seguroEfetivo, seguroForcadoPos, taxaSeguro,
    percLanceFidelidade: percLanceFidelidadeIn,
  } = input;

  const percLanceLivre = parseFloat(lanceLivre) || 0;
  const percLanceEmbutido = parseFloat(lanceEmbutido) || 0;
  const percLanceFixo = Math.max(0, Number(fixedLancePercent) || 0);
  const percLanceFidelidade = Math.max(0, Number(percLanceFidelidadeIn) || 0);
  const numJuncao = parseInt(juncaoCota) || 1;

  // Crédito total considerando junção de cotas
  const valorCreditoJunto = valorCredito * numJuncao;

  // Parcela base (por cota)
  const parcelaBase = parcelaSemSeguro;

  // Aplicar desconto Flex + seguro (por cota). calcParcelaFlex com 0% == normal,
  // então o condicional isFlex/desconto>0 colapsa pra um só call.
  const descontoFlexValor = parseFloat(descontoFlex) || 0;
  const parcelaComSeguro = calcParcelaFlex(
    { parcelaSemSeguro: parcelaBase, valorSeguro, hasInsurance: seguroEfetivo === 'Sim' },
    isFlex ? descontoFlexValor : 0,
  );

  // Parcela total pré-contemplação (todas as cotas juntas)
  const parcelaJunta = parcelaComSeguro * numJuncao;

  // Parcela flex SEM seguro, junta — é o que de fato é pago por mês antes da
  // contemplação e, portanto, o que amortiza o saldo. (Sem flex == parcela cheia.)
  const parcelaPagaSemSeguroJunta =
    calcParcelaFlex(
      { parcelaSemSeguro: parcelaBase, valorSeguro, hasInsurance: false },
      isFlex ? descontoFlexValor : 0,
    ) * numJuncao;

  // Valor total da categoria = Crédito total × FatorTaxa (base dos lances Servopa).
  const fatorTaxa = 1 + (taxaAdmin / 100);
  const valorTotalComTaxa = valorCreditoJunto * fatorTaxa;

  // Amortização pré-contemplação = soma do que foi efetivamente pago (parcela flex).
  // Com flex você paga menos por mês → amortiza menos → saldo na contemplação maior,
  // e a diferença é diluída na parcela pós (que fica acima da parcela flex).
  const mesesPagosPre = Math.min(Math.max(0, mesesContemplacao), prazo);
  const amortizadoPreContemplacao = parcelaPagaSemSeguroJunta * mesesPagosPre;
  const saldoNaContemplacao = Math.max(0, valorTotalComTaxa - amortizadoPreContemplacao);

  // Parcela principal pós-contemplação (sem seguro), base p/ converter lance em parcelas.
  // A Servopa arredonda o % do saldo devedor a 6 casas ANTES de multiplicar pelo crédito
  // (planilha "Cálculo_Lance": O12=ROUND(saldo%/(n−m);6) → O13=ROUND(crédito×O12;6)) — é o
  // que faz parcela/lance baterem centavo a centavo. Ela não muda com o lance: o lance abate
  // saldo E prazo na mesma proporção, mantendo a mensalidade ~constante.
  const parcelasRestantesSorteio = Math.max(0, prazo - mesesContemplacao);
  const temParcelasPosContemplacao = parcelasRestantesSorteio > 0;
  const parcelaPrincipalPos = temParcelasPosContemplacao && valorCreditoJunto > 0
    ? round6(valorCreditoJunto * round6(saldoNaContemplacao / valorCreditoJunto / parcelasRestantesSorteio))
    : 0;

  const lanceFixoCalc = lanceEmParcelas(percLanceFixo, valorTotalComTaxa, parcelaPrincipalPos);
  // Fidelidade é a MESMA conta do fixo — muda só o percentual, que aqui é
  // escolhido (30/20/15/10) em vez de vir do grupo. Reusar lanceEmParcelas em
  // vez de recalcular garante que as duas modalidades nunca divirjam por causa
  // de arredondamento.
  const lanceFidelidadeCalc = lanceEmParcelas(
    percLanceFidelidade,
    valorTotalComTaxa,
    parcelaPrincipalPos,
  );
  const lanceEmbutidoCalc = lanceEmParcelas(percLanceEmbutido, valorTotalComTaxa, parcelaPrincipalPos);
  // Lance livre: ofertado = recurso próprio + embutido, arredondado a parcelas DE UMA VEZ
  // (planilha D18); o recurso próprio = ofertado total − embutido (planilha D20 = D18 − D19).
  const ofertadoLivreCalc = lanceEmParcelas(percLanceLivre + percLanceEmbutido, valorTotalComTaxa, parcelaPrincipalPos);

  // taxa_seguro vem do banco em formato percentual (ex: 0.04 = 0.04%); converte p/ decimal.
  const TAXA_SEGURO = (Number.isFinite(taxaSeguro as number) ? (taxaSeguro as number) : DEFAULT_TAXA_SEGURO_PCT) / 100;
  // Seguro pós incide quando há seguro (auto ou manual, indistintamente).
  const temSeguroPos = seguroEfetivo === 'Sim' || seguroForcadoPos;
  const fatorSeguro = temSeguroPos ? TAXA_SEGURO : 0;
  const seguroDe = (saldo: number) => temParcelasPosContemplacao ? fatorSeguro * Math.max(0, saldo) : 0;
  const parcelaPosDe = (saldo: number, parcelas: number) =>
    parcelas > 0 ? Math.max(0, saldo) / parcelas + seguroDe(saldo) : 0;

  // Sorteio: sem lances.
  const creditoSorteio = valorCreditoJunto;
  const saldoDevedorSorteio = saldoNaContemplacao;
  const seguroSorteio = seguroDe(saldoDevedorSorteio);
  const parcelaAPagarSorteio = parcelaPosDe(saldoDevedorSorteio, parcelasRestantesSorteio);

  // Lance Fixo: percentual fixo do grupo, embutido (sai do crédito). Abate saldo e prazo.
  const creditoLanceFixo = valorCreditoJunto - lanceFixoCalc.valor;
  const saldoDevedorLanceFixo = Math.max(0, saldoNaContemplacao - lanceFixoCalc.valor);
  const parcelasRestantesLanceFixo = Math.max(0, parcelasRestantesSorteio - lanceFixoCalc.parcelas);
  const seguroLanceFixo = seguroDe(saldoDevedorLanceFixo);
  const parcelaAPagarLanceFixo = parcelaPosDe(saldoDevedorLanceFixo, parcelasRestantesLanceFixo);

  // Lance Fidelidade: idêntico ao fixo em comportamento — embutido, abate saldo
  // e prazo. O que muda é de onde vem o percentual.
  const creditoLanceFidelidade = valorCreditoJunto - lanceFidelidadeCalc.valor;
  const saldoDevedorLanceFidelidade = Math.max(0, saldoNaContemplacao - lanceFidelidadeCalc.valor);
  const parcelasRestantesLanceFidelidade = Math.max(
    0,
    parcelasRestantesSorteio - lanceFidelidadeCalc.parcelas,
  );
  const seguroLanceFidelidade = seguroDe(saldoDevedorLanceFidelidade);
  const parcelaAPagarLanceFidelidade = parcelaPosDe(
    saldoDevedorLanceFidelidade,
    parcelasRestantesLanceFidelidade,
  );

  // Lance Livre: embutido (sai do crédito) + recurso próprio (pago à vista). O ofertado total
  // abate saldo E prazo (encurta — a parcela fica ~constante, como no Lance Fixo); só o
  // embutido reduz o crédito disponível. Recurso próprio = ofertado total − embutido.
  const valorLanceLivre = Math.max(0, ofertadoLivreCalc.valor - lanceEmbutidoCalc.valor);
  const creditoLanceLivre = valorCreditoJunto - lanceEmbutidoCalc.valor;
  const saldoDevedorLanceLivre = Math.max(0, saldoNaContemplacao - ofertadoLivreCalc.valor);
  const parcelasRestantesLanceLivre = Math.max(0, parcelasRestantesSorteio - ofertadoLivreCalc.parcelas);
  const seguroLanceLivre = seguroDe(saldoDevedorLanceLivre);
  const parcelaAPagar = parcelaPosDe(saldoDevedorLanceLivre, parcelasRestantesLanceLivre);

  return {
    valorCreditoJunto,
    parcelaJunta,
    parcelaAPagar,
    parcelasRestantes: parcelasRestantesSorteio,
    parcelasRestantesSorteio,
    parcelasRestantesLanceFixo,
    parcelasRestantesLanceLivre,
    valorLanceLivre,
    valorLanceEmbutido: lanceEmbutidoCalc.valor,
    valorLanceFixo: lanceFixoCalc.valor,
    lanceLivrePercent: percLanceLivre,
    lanceEmbutidoPercent: percLanceEmbutido,
    lanceOfertadoPercent: percLanceLivre + percLanceEmbutido,
    parcelasLanceOfertado: ofertadoLivreCalc.parcelas,
    parcelasLanceEmbutido: lanceEmbutidoCalc.parcelas,
    parcelaPrincipalPos,
    passoLancePct: valorTotalComTaxa > 0 ? (parcelaPrincipalPos / valorTotalComTaxa) * 100 : 0,
    lanceFixoPercent: percLanceFixo,
    creditoSorteio,
    saldoDevedorSorteio,
    parcelaAPagarSorteio,
    seguroSorteio,
    creditoLanceFixo,
    saldoDevedorLanceFixo,
    parcelaAPagarLanceFixo,
    seguroLanceFixo,
    valorLanceFidelidade: lanceFidelidadeCalc.valor,
    lanceFidelidadePercent: percLanceFidelidade,
    creditoLanceFidelidade,
    saldoDevedorLanceFidelidade,
    parcelaAPagarLanceFidelidade,
    seguroLanceFidelidade,
    parcelasRestantesLanceFidelidade,
    creditoLanceLivre,
    saldoDevedorLanceLivre,
    parcelaAPagarLanceLivre: parcelaAPagar,
    seguroLanceLivre,
    parcelaComSeguro,
  };
}
